import React from 'react';
import LegalLayout from '../components/LegalLayout';

const PrivacyPolicy = () => (
  <LegalLayout title="Política de Privacidad" subtitle="Legal" lastUpdated="13 de mayo de 2026">
    <p>
      En <strong>Teko</strong> tomamos la privacidad de nuestros usuarios con la
      máxima seriedad. La presente Política de Privacidad explica qué datos
      personales recolectamos, cómo los utilizamos, con quién los compartimos y
      los derechos que asisten al usuario respecto de los mismos. Al utilizar
      Teko, usted acepta las prácticas descritas en esta Política de Privacidad.
    </p>

    <h2>1. Información que recolectamos</h2>
    <p>Para el correcto funcionamiento de Teko, recolectamos los siguientes datos:</p>
    <ul>
      <li>
        <strong>Datos de cuenta:</strong> nombre, dirección de correo
        electrónico y contraseña (almacenada de forma encriptada mediante
        algoritmos de hashing seguros).
      </li>
      <li>
        <strong>Contenido generado por el usuario:</strong> hábitos, metas,
        eventos del calendario, entradas del diario, ideas de claridad mental,
        imágenes y datos del vision board, registros de presupuesto, ahorros y
        deudas.
      </li>
      <li>
        <strong>Datos de uso:</strong> fecha y hora de los check-ins, créditos
        de inteligencia artificial consumidos, eventos completados, racha de
        hábitos y métricas necesarias para mostrar las estadísticas
        correspondientes al usuario.
      </li>
      <li>
        <strong>Datos de pago:</strong> en caso de suscripción a un plan pago,
        los datos de la tarjeta NO son procesados ni almacenados en nuestros
        servidores, sino directamente por el{' '}
        <strong>proveedor de servicios de pago autorizado</strong>. Teko
        únicamente conserva el identificador de la suscripción, el plan
        contratado, su estado y la fecha del próximo cobro.
      </li>
      <li>
        <strong>Datos técnicos:</strong> dirección IP, navegador, dispositivo y
        marcas temporales, utilizados exclusivamente con fines de seguridad y
        prevención de abusos.
      </li>
    </ul>

    <h2>2. Cómo utilizamos los datos</h2>
    <ul>
      <li>Para garantizar el funcionamiento de Teko (visualización de hábitos, metas, calendario, entre otros).</li>
      <li>Para generar respuestas de inteligencia artificial personalizadas (resúmenes, sugerencias de pasos, reflexiones del diario).</li>
      <li>Para procesar los pagos a través de la pasarela de pagos utilizada.</li>
      <li>Para enviar notificaciones dentro de la aplicación (hábitos pendientes, eventos próximos, metas próximas a vencer).</li>
      <li>Para mejorar la plataforma a partir de métricas anónimas y agregadas.</li>
      <li>Para responder consultas recibidas mediante el formulario de contacto.</li>
    </ul>

    <h2>3. Servicios de terceros</h2>
    <ul>
      <li>
        <strong>Proveedor de servicios de pago:</strong> procesador de pagos
        autorizado. La información financiera del usuario es gestionada
        exclusivamente por dicho proveedor, conforme a sus propias políticas
        de seguridad (PCI-DSS).
      </li>
      <li>
        <strong>Proveedores de inteligencia artificial (OpenAI, Anthropic,
        Google):</strong> las consultas enviadas a las funciones de
        inteligencia artificial pueden ser procesadas por estos servicios. No
        se les transmite información personal identificable más allá del texto
        necesario para generar la respuesta.
      </li>
      <li>
        <strong>MongoDB Atlas:</strong> base de datos en la que se almacena la
        información del usuario, cifrada tanto en tránsito como en reposo.
      </li>
    </ul>

    <h2>4. Compartir los datos</h2>
    <p>
      <strong>No vendemos ni alquilamos los datos personales de los usuarios.</strong>{' '}
      Únicamente compartimos información con los proveedores estrictamente
      necesarios para la operación de Teko (mencionados anteriormente) y cuando
      así lo requiera una autoridad judicial conforme a la ley aplicable.
    </p>

    <h2>5. Derechos del usuario</h2>
    <p>El usuario tiene derecho a:</p>
    <ul>
      <li><strong>Acceder</strong> a sus datos personales en cualquier momento.</li>
      <li><strong>Rectificar</strong> la información incorrecta editando su perfil o solicitándolo por correo electrónico.</li>
      <li><strong>Eliminar</strong> su cuenta y la totalidad de los datos asociados (con excepción de los registros mínimos requeridos por la normativa fiscal aplicable en caso de pagos).</li>
      <li><strong>Solicitar</strong> una copia de sus datos personales escribiendo a <a href="mailto:asistencia.teko@gmail.com">asistencia.teko@gmail.com</a>.</li>
      <li><strong>Oponerse</strong> al uso de sus datos con fines de mejora del producto.</li>
    </ul>
    <p>
      Para ejercer cualquiera de estos derechos, el usuario podrá escribir a{' '}
      <a href="mailto:asistencia.teko@gmail.com">asistencia.teko@gmail.com</a>.
      Las solicitudes serán respondidas en un plazo máximo de 15 días hábiles.
    </p>

    <h2>6. Seguridad</h2>
    <p>
      Las contraseñas se encriptan mediante bcrypt, el tráfico se transmite a
      través de HTTPS (TLS 1.2 o superior) y los servidores requieren
      autenticación. No obstante estas medidas, ningún sistema digital es
      completamente seguro. En caso de detectarse una vulneración que afecte
      los datos del usuario, se notificará por correo electrónico dentro de las
      72 horas siguientes.
    </p>

    <h2>7. Retención</h2>
    <p>
      Los datos del usuario se conservan mientras la cuenta permanezca activa.
      En caso de eliminación de la cuenta, los datos se eliminarán de forma
      permanente dentro de los 30 días siguientes, con excepción de la
      información cuya conservación sea exigida por la legislación fiscal
      paraguaya por el plazo legal mínimo aplicable.
    </p>

    <h2>8. Menores de edad</h2>
    <p>
      Teko está dirigido a personas mayores de 13 años. Los usuarios menores de
      18 años deberán contar con la autorización y supervisión de sus padres,
      tutores o representantes legales. Si detectamos que una cuenta pertenece
      a un menor que no cumple estas condiciones, podremos suspenderla o
      eliminarla.
    </p>

    <h2>9. Cambios a esta política</h2>
    <p>
      En caso de modificarse la presente Política de Privacidad, se notificará
      al usuario mediante correo electrónico y se mostrará un aviso visible en
      la aplicación en su próximo ingreso. La fecha de "Última actualización"
      indicada al inicio del documento será actualizada en consecuencia.
    </p>

    <h2>10. Contacto</h2>
    <p>
      Para cualquier consulta sobre esta Política de Privacidad o sobre el
      tratamiento de los datos personales, el usuario podrá escribir a{' '}
      <a href="mailto:asistencia.teko@gmail.com">asistencia.teko@gmail.com</a>.
    </p>
  </LegalLayout>
);

export default PrivacyPolicy;
